'use client';

import { EyeOff, Eye } from 'lucide-react';
import React, { useState } from 'react';
import { NumericFormat } from 'react-number-format';

import { FieldErrorMessage } from '@/components/ui/FormFields/FieldErrorMessage';
import { Label } from '@/components/ui/FormFields/Label';
import { changedFieldClassName, useIsChangedField } from '@/components/shared/FormChangeHighlightContext';
import { useRequiredField } from '@/components/shared/FormRequiredFieldsContext';
import { cn } from '@/lib/utils';

export type InputFieldProps = {
  id: string;
  label?: string;
  description?: string;
  error?: string;
  containerClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'defaultValue'>;

const Input = React.forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      id,
      label,
      description,
      error,
      type,
      placeholder = 'type...',
      containerClassName = 'space-y-2',
      labelClassName,
      inputClassName,
      onChange,
      required,
      value,
      ...props
    },
    ref
  ) => {
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const fieldName = typeof props.name === 'string' ? props.name : null;
    const isRequired = useRequiredField(fieldName, required);
    const isChanged = useIsChangedField(fieldName);

    const handleTogglePasswordVisibility = () => {
      setIsPasswordVisible((prevState) => !prevState);
    };

    const getInputType = () => {
      if (type === 'password') {
        return isPasswordVisible ? 'text' : 'password';
      }

      return type;
    };

    const renderInput = () => {
      const isPercentageType = type === 'percentage-decimal-number';
      const isDecimal = type === 'decimal-number' || isPercentageType;

      const commonProps = {
        id,
        placeholder,
        className: cn(
          'border-tablesDivider ring-offset-background flex h-8 w-full placeholder:text-gray600 rounded-sm border bg-transparent px-3 py-2 text-sm file:border-1 file:bg-transparent file:text-sm file:font-medium disabled:cursor-not-allowed bg-inputBackground disabled:opacity-50',
          isChanged && changedFieldClassName,
          inputClassName
        ),
        required: isRequired,
        ...props,
      };

      if (type === 'number' || isDecimal) {
        return (
          <NumericFormat
            getInputRef={ref}
            value={value as string | number | undefined}
            thousandSeparator={isDecimal ? ',' : undefined}
            decimalScale={isDecimal ? 2 : undefined}
            fixedDecimalScale={isDecimal}
            allowNegative={false}
            onValueChange={(values) => {
              if (onChange) {
                const event = {
                  target: {
                    id,
                    value: values.value,
                    name: props.name,
                  },
                } as React.ChangeEvent<HTMLInputElement>;

                onChange(event);
              }
            }}
            {...commonProps}
          />
        );
      }

      return (
        <input ref={ref} type={getInputType()} onChange={onChange} autoComplete="off" value={value} {...commonProps} />
      );
    };

    return (
      <div className={cn('w-full', containerClassName)}>
        {label && (
          <div className="flex flex-col space-y-2">
            <Label
              htmlFor={id}
              required={isRequired}
              className={cn('text-primary text-xs font-semibold', labelClassName)}
            >
              {label}
            </Label>
            {description && <span className="text-gray800 text-xs">{description}</span>}
          </div>
        )}

        <div className="relative">
          {renderInput()}

          {type === 'percentage-decimal-number' && (
            <span className="absolute inset-y-0 right-2 flex items-center text-gray900 pointer-events-none text-sm">
              %
            </span>
          )}

          {type === 'password' && (
            <button
              type="button"
              onClick={handleTogglePasswordVisibility}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
            >
              {isPasswordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          )}
        </div>

        <FieldErrorMessage message={error} />
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
